// @flow

import React, {useMemo, useState} from 'react';
import {
  Button,
  Divider,
  IconButton,
  makeStyles,
  Tooltip,
  Typography,
} from '@material-ui/core';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {colors} from '../../../../../../assets/styles/colors';
import ChatFilter from './ChatFilter/ChatFilter';

import type {Theme} from '@material-ui/core';
import {useCollaborators} from '../../../../../configure/accounts/utils';
import {
  useFilterConversations,
  useFilterConversationsActions,
  useInitializeConversationCount,
} from '../../../utils/conversation';
import LogoutIcon from '../../../../../../elements/icons/LogoutIcon';
import GroupIcon from '../../../../../../elements/icons/GroupIcon';
import {SmsOutlined} from '@material-ui/icons';
import {useStoreDevices, formatStoreDeviceIdentifier} from '../../../utils';
import {getMultichatServiceIcon} from '../../../../utils/service-icon';
import type {StoreDevice} from '../../../../../../entities';

type Props = {
  expanded: boolean,
  toggleExpanded: () => void,
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    height: '100%',
    width: '100%',
    borderRight: '1px solid #E8E8ED',
    padding: '.75rem',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '.5rem',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',

    justifyContent: 'center',

    '&[data-expanded="true"]': {
      justifyContent: 'space-between',
    },
  },
  innerDivider: {
    border: '1px dashed #E8E8ED',
    backgroundColor: 'unset',
    width: '100%',
  },
  boldText: {
    fontSize: 14,
    fontWeight: 600,
    color: colors.grey900,
  },
  filterWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    width: '100%',
  },
  collaborators: {
    width: '100%',
  },
  collaboratorsList: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  groupIcon: {
    color: colors.grey700,
    fontSize: '1.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '.5rem',
  },
  expandButton: {
    textTransform: 'none',
    '& span': {
      justifyContent: 'flex-start',
    },
  },
  expandInboxButton: {
    padding: '.5rem',
    '&[data-expanded="false"]': {
      transform: 'rotate(180deg)',
    },
  },
  tooltip: {
    background: colors.grey700,
    boxShadow: '0px 4px 12px rgba(14, 21, 58, 0.08)',
    borderRadius: '4px',
    padding: '.5rem .75rem',
    color: 'white',
    fontSize: '.85rem',
    fontWeight: 500,
  },
  arrow: {
    color: colors.grey700,
  },
  channelIcon: {
    width: '1.5rem',
    height: '1.5rem',
    flexShrink: 0,

    '& img': {
      transition: 'transform .2s ease-in-out',
      width: '100%',
      height: '100%',
    },
  },
}));

function getDeviceAccountName(device: StoreDevice): string {
  return formatStoreDeviceIdentifier(device)
    .replace(/^Número:\s*/, '')
    .replace(/^Cuenta:\s*/, '');
}

export default function InboxGrid(props: Props) {
  const {expanded, toggleExpanded} = props;

  const classes = useStyles();

  const [expandCollabs, setExpandCollabs] = useState(true);
  const [expandChannels, setExpandChannels] = useState(true);

  const {storeDevices} = useStoreDevices();

  const connectedDevices = useMemo(
    () => storeDevices.filter(device => device.state === 'CONNECTED'),
    [storeDevices]
  );

  const {collaborators} = useCollaborators();

  const {
    admin,
    collaborators: collaboratorsCounts,
    total,
    requiresAssistanceCount,
  } = useInitializeConversationCount();

  const {
    admin: adminFilter,
    collaborators: collaboratorsFilter,
    requiresAssistance: requiresAssistanceFilter,
    deviceId: deviceIdFilter,
  } = useFilterConversations();

  const {
    filterByAdmin,
    filterByCollaborator,
    filterByServices,
    filterByRequiresAssistance,
  } = useFilterConversationsActions();

  const allSelected =
    !adminFilter &&
    (!collaboratorsFilter || collaboratorsFilter.length === 0) &&
    !requiresAssistanceFilter;

  const allChannelsSelected = !deviceIdFilter;

  return (
    <div className={classes.root}>
      <div className={classes.content}>
        <div className={classes.row} data-expanded={expanded}>
          {expanded && (
            <Typography className={classes.boldText}>Inbox</Typography>
          )}
          <Tooltip
            title={expanded ? 'Ocultar Inbox' : 'Mostrar Inbox'}
            arrow
            placement="right"
            classes={{
              tooltip: classes.tooltip,
              arrow: classes.arrow,
            }}
          >
            <IconButton
              size="small"
              onClick={toggleExpanded}
              data-expanded={expanded}
              className={classes.expandInboxButton}
            >
              <LogoutIcon />
            </IconButton>
          </Tooltip>
        </div>
        <div className={classes.filterWrap}>
          <ChatFilter
            name="Todos"
            count={total}
            showName={expanded}
            selected={!!allSelected}
            onClick={() => {
              if (allSelected) return;
              filterByCollaborator([]);
              filterByAdmin(false);
              filterByServices([]);
              filterByRequiresAssistance(false);
            }}
          />
          <ChatFilter
            name="Míos"
            count={admin}
            showName={expanded}
            selected={adminFilter}
            onClick={() => {
              filterByAdmin(!adminFilter);
            }}
          />
          <ChatFilter
            name="Atender"
            count={requiresAssistanceCount}
            showName={expanded}
            selected={requiresAssistanceFilter}
            onClick={() => {
              filterByRequiresAssistance(!requiresAssistanceFilter);
            }}
          />
        </div>
        <Divider className={classes.innerDivider} />
        <div className={classes.collaborators}>
          {expanded ? (
            <Button
              disableElevation
              className={classes.expandButton}
              fullWidth
              onClick={() => setExpandCollabs(ps => !ps)}
            >
              {expandCollabs ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              Colaboradores
            </Button>
          ) : (
            <span className={classes.groupIcon}>
              <GroupIcon />
            </span>
          )}
          {expandCollabs && (
            <div className={classes.collaboratorsList}>
              {collaborators.map(collaborator => {
                const collaboratorCount = collaboratorsCounts.find(
                  ({id}) => collaborator.id === id
                );

                const selected =
                  collaboratorsFilter &&
                  collaboratorsFilter.includes(collaborator.id);

                return (
                  <ChatFilter
                    key={collaborator.id}
                    count={collaboratorCount ? collaboratorCount.count : 0}
                    name={collaborator.firstName}
                    selected={selected}
                    onClick={() => {
                      if (selected) return;
                      filterByCollaborator([collaborator.id]);
                    }}
                    showName={expanded}
                  />
                );
              })}
            </div>
          )}
        </div>

        <Divider className={classes.innerDivider} />
        <div className={classes.collaborators}>
          {expanded ? (
            <Button
              disableElevation
              className={classes.expandButton}
              fullWidth
              onClick={() => setExpandChannels(ps => !ps)}
            >
              {expandChannels ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              Canales
            </Button>
          ) : (
            <span className={classes.groupIcon}>
              <SmsOutlined />
            </span>
          )}
          {expandChannels && (
            <div className={classes.collaboratorsList}>
              <ChatFilter
                name="Todos"
                selected={allChannelsSelected}
                onClick={() => {
                  filterByServices([]);
                }}
                showName={expanded}
                collapsedContent={<span>Todos</span>}
              />

              {connectedDevices.map(device => {
                const accountName = getDeviceAccountName(device);
                const selected = deviceIdFilter === device.id;
                const channelIcon = (
                  <picture className={classes.channelIcon}>
                    <img
                      src={getMultichatServiceIcon(device.service)}
                      alt={accountName}
                    />
                  </picture>
                );

                return (
                  <ChatFilter
                    key={device.id}
                    name={accountName}
                    selected={selected}
                    onClick={() => {
                      filterByServices([device.service], device.id);
                    }}
                    icon={channelIcon}
                    showName={expanded}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
